import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { getTranslation } from '../translations';

/**
 * EducationAuthWatcher — Sprint 38 P1 Fix 3 + Track C
 *
 * Listens for the `shs:auth-expired` event dispatched by `utils/auth.js`
 * (on 401 clearing an expired/invalid token) and by
 * `EducationAuthContext.fetchUser` (proactive isTokenExpired guard).
 *
 * On event:
 *   1. Fires a one-time bilingual toast: "Your session expired. Please
 *      sign in again." / "Tu sesión expiró. Inicia sesión nuevamente."
 *   2. Sets a transient sessionStorage flag `shs:session-expired-notice`
 *      that the EducationLoginPage picks up and renders as an accessible
 *      role="status" banner, then clears. The flag lives in sessionStorage
 *      so it does NOT persist across unrelated future visits (cleared on
 *      tab close AND cleared on read by the login page).
 *   3. Navigates to /education/login with state.from set to the current
 *      protected path (safe return-path preserved).
 *
 * Public auth routes (login/register/reset-password) are excluded so we
 * don't loop or shout at users who intentionally reached the login page
 * with no session.
 */
const PUBLIC_PREFIXES = [
  '/education/login',
  '/education/register',
  '/education/reset-password',
  '/education/verify-email',
];

const NOTICE_KEY = 'shs:session-expired-notice';

export default function EducationAuthWatcher() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleAuthExpired = (event) => {
      const currentPath =
        (location && location.pathname ? location.pathname : '/') +
        (location && location.search ? location.search : '');
      if (PUBLIC_PREFIXES.some((p) => currentPath.startsWith(p))) {
        return;
      }
      const detailFrom = event?.detail?.from;
      const from = detailFrom || currentPath || '/';

      // Track C — resolve language from localStorage (same source the
      // LanguageContext reads) so the notice matches the user's UI language
      // without requiring a hook subscription here.
      let language = 'en';
      try {
        language = localStorage.getItem('language') || 'en';
      } catch (_e) { /* silent */ }

      const noticeText = getTranslation(language, 'auth.sessionExpired');

      // Record a one-time notice for the login page to render as a banner.
      // sessionStorage keeps it tab-scoped and non-persistent.
      try {
        sessionStorage.setItem(
          NOTICE_KEY,
          JSON.stringify({ text: noticeText, at: Date.now(), language }),
        );
      } catch (_e) { /* silent */ }

      // Also surface a toast — non-persistent, accessible via sonner defaults.
      try {
        toast.message(noticeText);
      } catch (_e) { /* silent */ }

      navigate('/education/login', { replace: true, state: { from } });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('shs:auth-expired', handleAuthExpired);
      return () => window.removeEventListener('shs:auth-expired', handleAuthExpired);
    }
    return undefined;
  }, [navigate, location]);

  return null;
}

// Expose the notice key so the login page can read + clear it.
export const SESSION_EXPIRED_NOTICE_KEY = NOTICE_KEY;
